import { t } from 'i18next';
import { type ChangeEvent, type DragEvent, useCallback, useRef, useState } from 'react';
import { generateRandomId } from '../../../lib/random';
import { cn } from '../../../lib/styles';
import { Alert, Button, Icon } from '../index.spark';
import { isAccepted } from './filter-file-type';
import type { FileUploadProps } from './types';

export function FileUpload({
  multi = false,
  accept = [],
  label,
  hintText,
  value,
  onChange,
  error,
  errorTitle,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilesSelected = useCallback(
    (fileList: FileList) => {
      const acceptedFiles = Array.from(fileList).filter((f) => isAccepted(f, accept));

      const newFiles = acceptedFiles.map((file) => ({
        id: generateRandomId(),
        file: file,
      }));

      if (multi) {
        onChange([...value, ...newFiles]);
      } else {
        onChange(newFiles.slice(0, 1));
      }
    },
    [value, multi, accept, onChange],
  );

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();

    // If the mouse is still over the drop zone or one of its children, do nothing
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }

    setIsDragging(false);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(e.dataTransfer.files);
    }
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelected(e.target.files);
      e.target.value = '';
    }
  }

  function handleContainerClick() {
    fileInputRef.current?.click();
  }

  function handleDelete(id: string) {
    const newFiles = value.filter((f) => f.id !== id);
    onChange(newFiles);
  }

  const uploadedFiles = (
    <div className="flex max-h-56 w-full flex-col gap-2 overflow-y-auto px-6 py-3">
      {value.map(({ file, id }) => (
        <div key={id} className="flex items-center gap-3 rounded-md">
          <Icon name="document" size="lg" />
          <div className="text-start">
            {file.name}
            <div className="text-spark-text-secondary text-xs">
              {file.size} {t('labels:bytes')}
            </div>
          </div>
          {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: Wrapped with e.stopPropagation as our Button doesn't support HTMLEvent in onClick */}
          <span className="ms-auto" onClick={(e) => e.stopPropagation()}>
            <Button icon="x" variant="bare" onClick={() => handleDelete(id)} />
          </span>
        </div>
      ))}
      <p className="text-center text-spark-text-secondary text-xs">
        {t('labels:file-upload', { count: multi ? 2 : 1 })}
      </p>
    </div>
  );

  const fileUploadButton = (
    <div className="flex flex-col items-center gap-1">
      <Button variant="secondary">{t('buttons:add-files', { count: multi ? 2 : 1 })}</Button>
      <p className="text-spark-text-secondary">{hintText}</p>
    </div>
  );

  return (
    <div className="flex flex-col gap-1 text-sm text-spark-text-primary">
      {label}
      {/* biome-ignore lint/a11y/useSemanticElements: We cannot have a button inside a button, so using a clickable div */}
      <div
        role="button"
        tabIndex={0}
        // Buttons trigger `onClick` on the following events:
        // `onKeyDown` for Enter key
        // `onKeyUp` for Space key
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleContainerClick();
          } else if (e.key === ' ') {
            // Stop page scrolling
            e.preventDefault();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ') {
            handleContainerClick();
          }
        }}
        onClick={handleContainerClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex min-h-32 cursor-pointer justify-center rounded-lg border-2 text-center transition',
          { 'items-center': value.length === 0 },
          isDragging
            ? 'border-spark-purple border-solid bg-violet-100'
            : 'border-spark-border-default border-dashed bg-white',
          (error || errorTitle) && 'border-red-300',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multi}
          accept={accept.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />

        {value.length > 0 ? uploadedFiles : fileUploadButton}
      </div>
      {(error || errorTitle) && (
        <Alert headingLevel="h1" variant="error" title={errorTitle}>
          {error}
        </Alert>
      )}
    </div>
  );
}
